export class DumbConfigSource {
    constructor(name, stage, values) {
        this.encrypted = false;
        this.loaded = true;
        this.url = '';
        this.urlPrefix = '';
        this.name = name;
        this.stage = stage;
        this.values = values;
        this.ts = Date.now();
    }
    async fetch(refresh) {
        return this.values;
    }
}
export class FetchConfigSource {
    constructor(nameOrOptions, stage) {
        var _a, _b;
        this._encrypted = false;
        this.urlPrefix = 'https://config.storyverse.xyz';
        this.backupUrlprefix = 'https://d3ar4smkmrrv66.cloudfront.net';
        this.stage = 'dev';
        this.ts = 0;
        this.loaded = false;
        this.headers = {};
        this.timeout = 10000;
        this.refreshInterval = 120 * 1000;
        this.values = undefined;
        this.headers = {};
        if (typeof nameOrOptions == 'string') {
            this.name = nameOrOptions;
            this.stage = stage !== null && stage !== void 0 ? stage : 'prod';
        }
        else {
            this.name = nameOrOptions.name;
            this.stage = (_b = (_a = nameOrOptions.stage) !== null && _a !== void 0 ? _a : stage) !== null && _b !== void 0 ? _b : 'prod';
            Object.assign(this, nameOrOptions);
        }
        if (this.encrypted) {
            //@ts-ignore
            if (!globalThis.decrypt) {
                throw new Error('global decrypt function must be provided when using encrypted config');
            }
            //@ts-ignore
            this.decrypt = globalThis.decrypt;
        }
    }
    get encrypted() {
        return this._encrypted;
    }
    set encrypted(encrypted) {
        this._encrypted = encrypted;
    }
    async fetchUrl(url) {
        // @ts-ignore 'fetch' doesnt exist without 'dom'
        const fetchFunc = typeof fetch == "function" ? fetch : globalThis.fetch;
        if (!fetchFunc) {
            throw new Error('fetch function not found. Must provide a polyfil with node-fetch or equivalent.');
        }
        const response = await fetchFunc(url, {
            headers: this.headers,
            keepalive: false,
            method: 'GET',
            mode: 'cors',
            cache: 'reload',
        });
        if (response.status >= 400 || response.status < 200) {
            throw new Error(`${response.status} ${response.statusText} fetching url ${url}`);
        }
        const data = this.encrypted
            ? JSON.parse(this.decrypt(await response.text()))
            : await response.json();
        let resHeaders = {};
        for (const [key, val] of response.headers.entries()) {
            resHeaders[key] = val;
        }
        return {
            data,
            status: response.status,
            headers: resHeaders,
        };
    }
    decrypt(data) {
        /** @ts-ignore consumer of this library responsible for providing decrypt function */
        if (this.encrypted) {
            //@ts-ignore
            return globalThis.decrypt(data);
        }
        else {
            return data;
        }
    }
    //get filename():string { return `${this.stage}-${this.name}` }
    get filename() { return `${this.name}${this.encrypted ? '' : '.json'}`; }
    get url() { return `${this.urlPrefix}/${this.stage}-${this.filename}`; }
    get file() { return `${this.cacheDir}/${this.stage}-${this.filename}`; }
    /** fetch the configs from the config service & cache the structure in memory.
     * The fetch and/or update are skipped under certain conditions:
     *
     * - `disabled` is truthy
     * - `url` is not set
     * - the config was already fetched recently (within `refreshInterval` ms) and refresh is false
     * - the server returns 304 to a if-none-match request (old config still valid)
     *
     * You can call the method `poll(interval)` to initialize or modify the periodic
     * polling interval, which will repeatedly call this method using a timer.
     */
    async fetch(refresh) {
        var _a;
        if (this.values) {
            if (!refresh)
                return this.values;
            if (Date.now() - this.ts < this.refreshInterval)
                return this.values;
        }
        this.ts = Date.now();
        const res = await this.fetchUrl(this.url);
        if (res.status == 304 && this.values) {
            return this.values;
        }
        if (res.data) {
            this.values = res.data;
            if (this.values && res.headers['etag']) {
                this.headers['If-None-Match'] = res.headers['etag'];
                this.headers['If-Modified-Since'] = (_a = res.headers['last-modified']) !== null && _a !== void 0 ? _a : res.headers['date'];
            }
        }
        return this.values || {};
    }
}
export class URLConfigSource extends FetchConfigSource {
    constructor(name, stage, url) {
        super(name, stage);
        this._url = url;
    }
    get url() {
        return this._url;
    }
}
/** load and periodically refresh configuration from a web service (or local json files) */
export class ConfigClient {
    constructor(stage, sourceClass) {
        /** auto-refresh interval millis */
        this.refreshInterval = 0;
        this.loaded = false;
        /** url to fetch updates */
        this.configSources = {};
        this.NS = {};
        this.cache = new Map();
        this.configUrlPrefix = "https://config.storyverse.xyz";
        /** disable fetching from the network */
        this.disabled = false;
        this.resolve = () => { };
        this.reject = () => { };
        this._stage = stage;
        if (stage in cfg) {
            // Unfortunately, without the following statement, we get a TypeScript error
            // TS2564: Property 'promise' has no initializer and is not definitely assigned in the constructor.
            this.promise = cfg[stage].promise;
            return cfg[stage];
        }
        else {
            cfg[stage] = this;
        }
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
        if (sourceClass) {
            this._sourceClass = sourceClass;
        }
        if (!cfg.cfg) {
            cfg.cfg = this;
        }
    }
    getStage() {
        return this._stage;
    }
    static client(stage, instance) {
        if (!cfg[stage] && instance) {
            cfg[stage] = instance;
        }
        return cfg[stage];
    }
    stage(_stage, cloneSources = true) {
        if (_stage != this._stage) {
            if (!cfg[_stage]) {
                const proto = Object.getPrototypeOf(this);
                const newClient = new proto.constructor(_stage, this._sourceClass);
                if (cloneSources) {
                    for (const src of this.sources) {
                        newClient.addSource(src.name, { encrypted: src.encrypted });
                    }
                }
                cfg[_stage] = newClient;
            }
            return cfg[_stage];
        }
        else if (cfg[_stage] !== this) {
            cfg[_stage] = this;
        }
        return this;
    }
    /**
     * Pass a class or constructor reference that will be used to create
     * ConfigSource instances.
     * A custom config source can be provided by the application in order to
     * customize how configs are fetched and/or cached. There is a different
     * implementation for back-end servers vs client browsers.
    */
    sourceClass(cls) {
        this._sourceClass = cls;
        return this;
    }
    get sources() {
        return Object.values(this.configSources);
    }
    /**
     * Add a config source by name or by passing one (or an array with multiple instances) of config source.
     * The second argument is an object with properties that get applied to the config source instance.
     */
    addSource(sources, options = {}) {
        const ctor = this._sourceClass;
        if (!Array.isArray(sources)) {
            sources = [sources];
        }
        for (let source of sources) {
            if (typeof source == 'string') {
                if (!ctor) {
                    // must either pre-configure the config source, or pass fully constructed instances.
                    throw new Error('sourceClass must be set before adding sources by name');
                }
                const name = source;
                if (this.configSources[name]) {
                    continue;
                }
                source = new ctor(Object.assign({ name, stage: this._stage, urlPrefix: this.configUrlPrefix }, options));
            }
            this.configSources[source.name] = source;
        }
        return this;
    }
    async loadAsync(refresh = false) {
        this.loadConfigs(refresh);
        return this.promise;
    }
    loadConfigs(refresh = false) {
        if (this.pending || (this.loaded && refresh == false)) {
            return this;
        }
        const promises = this.sources.map((source) => source.fetch(refresh));
        const pending = Promise.allSettled(promises).then((results) => {
            let loaded = null;
            for (const res of results) {
                if (res.status == 'fulfilled') {
                    loaded = loaded == null ? true : loaded;
                    Object.assign(this.NS, res.value);
                    Object.assign(this, res.value);
                }
                else {
                    loaded = false;
                    console.warn('config promise rejected:', res);
                }
            }
            this.loaded = loaded !== null && loaded !== void 0 ? loaded : false;
            this.pending = undefined;
            return this;
        }).then((value) => this.resolve(value))
            .catch((err) => this.reject(err));
        this.pending = pending;
        return this;
    }
    update(values) {
        Object.assign(this, values);
        return this;
    }
    /**
     * repeatedly poll for updates after a given interval (given in seconds)
     * Pass `refreshIntervalSeconds=0` to disable polling.
     */
    poll(refreshIntervalSeconds = 120) {
        if (this.disabled) {
            return this;
        }
        this.refreshInterval = refreshIntervalSeconds * 1000;
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        if (!refreshIntervalSeconds) {
            return this;
        }
        this.intervalId = setInterval(() => { this.loadConfigs(true); }, this.refreshInterval);
        return this;
    }
}
export const cfg = {};
//# sourceMappingURL=config.js.map